import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { FaChevronDown } from 'react-icons/fa';

import Switch from '../../Switch.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { serverMessage } from '../../../_helpers/errorMessage.js';
//import '../../styles/partial/modal/EditEventModal.css';

const getNextHourDateTime = () => {
  const nextHour = new Date();
  nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
  const pad = (num) => (num < 10 ? '0' + num : num);
  return `${nextHour.getFullYear()}-${pad(nextHour.getMonth() + 1)}-${pad(nextHour.getDate())}T${pad(nextHour.getHours())}:${pad(nextHour.getMinutes())}`;
};

/**
 * The admission price as sent (ENG-511): an empty field is 0, and anything
 * else must be a non-negative amount with at most two decimal places. The
 * input's min/step/pattern never applied, since Save is not a submit button.
 * Returns { value } or { error } (a catalog key).
 */
export const parseAdmissionPrice = (raw) => {
  const text = String(raw ?? '').trim();
  if (text === '') return { value: 0 };
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return { error: 'events.form.errors.priceInvalid' };
  return { value: Number(text) };
};

const EditEventModal = ({ bandEvent, isOpen, onClose, onSave }) => {
  const t = useT();
  const [id, setId] = useState(0);
  const [bandId, setBandId] = useState(0);
  const [title, setTitle] = useState('');
  const [dateTime, setDateTime] = useState(getNextHourDateTime());
  const [location, setLocation] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [isInPerson, setIsInPerson] = useState(false);
  const [followersOnly, setFollowersOnly] = useState(false);
  const [subscribersOnly, setSubscribersOnly] = useState(false);
  // Whether fans may request a song again after it has been played during this
  // event. A per-show setting (ENG-352, moved here from the band form), so a
  // new event starts permissive and an existing one keeps what was saved.
  const [allowRepeatRequests, setAllowRepeatRequests] = useState(true);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [cost, setCost] = useState(0);
  const [defaultSongSet, setDefaultSongSet] = useState('');
  const [startedAt, setStartedAt] = useState(null);
  const [endedAt, setEndedAt] = useState(null);
  const [isLoading, setLoading] = useState(false);
  // Why the last save failed (ENG-510): onSave rejects, and the modal stays
  // open with the server's words or a generic message.
  const [saveError, setSaveError] = useState(null);
  const savingRef = useRef(false);
  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const formatDateTimeLocal = (dateTime) => {
    const date = new Date(dateTime);
    const pad = (num) => (num < 10 ? '0' + num : num);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const validate = () => {
    const newErrors = {};
    if (!title?.trim()) newErrors.title = t('events.form.errors.titleRequired');
    if (!dateTime) newErrors.dateTime = t('events.form.errors.dateTimeRequired');
    if (isInPerson && !location?.trim()) newErrors.location = t('events.form.errors.locationRequired');
    if (!isOnline && !isInPerson) newErrors.eventType = t('events.form.errors.eventTypeRequired');
    const price = parseAdmissionPrice(cost);
    if (price.error) {
      newErrors.cost = t(price.error);
      // The price is under "More options"; open them so the message is seen.
      setShowAdvanced(true);
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // The save is awaited (ENG-510): Save shows the spinner until it settles,
  // a second click meanwhile is ignored, and a failure keeps the modal open.
  // It used to fire onSave and clear the spinner in the same tick, so a
  // double click created two events and a failure only reached the console.
  const handleSave = async () => {
    if (savingRef.current) return;
    if (!validate()) return;

    savingRef.current = true;
    setLoading(true);
    setSaveError(null);
    
    // A field belongs to its event type (ENG-511): switched off, the type's
    // fields go out cleared rather than keeping what was typed before.
    const eventData = {
      id,
      bandId,
      title: title.trim(),
      dateTime: new Date(dateTime).toISOString(),
      location: isInPerson ? (location ?? '').trim() : '',
      isOnline,
      isInPerson,
      followersOnly: isOnline ? followersOnly : false,
      subscribersOnly: isOnline ? subscribersOnly : false,
      allowRepeatRequests,
      additionalInfo,
      cost: parseAdmissionPrice(cost).value,
      defaultSongSet,
      startedAt,
      endedAt,
    };
    try {
      await onSave(eventData);
    } catch (err) {
      console.error('Error saving event:', err);
      setSaveError(serverMessage(err) || t('events.form.errors.saveFailed'));
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) setSaveError(null);
  }, [isOpen]);

  useEffect(() => {
      if (bandEvent) {
        setId(bandEvent.id);
        setBandId(bandEvent.bandId);
        setTitle(bandEvent.title);
        setDateTime(formatDateTimeLocal(bandEvent.dateTime));
        setLocation(bandEvent.location);
        setIsOnline(bandEvent.isOnline);
        setIsInPerson(bandEvent.isInPerson);
        setFollowersOnly(bandEvent.followersOnly);
        setSubscribersOnly(bandEvent.subscribersOnly);
        setAllowRepeatRequests(
          bandEvent.allowRepeatRequests ?? bandEvent.allow_repeat_requests ?? true
        );
        setAdditionalInfo(bandEvent.additionalInfo);
        setCost(bandEvent.cost);
        setDefaultSongSet(bandEvent.defaultSongSet);
        setStartedAt(bandEvent.startedAt ?? null);
        setEndedAt(bandEvent.endedAt ?? null);
      } else if (isOpen) {
        // Adding an event: the same modal instance is reused for every "Add
        // Event", so start from a clean form rather than the previous event
        setId(0);
        setBandId(0);
        setTitle('');
        setDateTime(getNextHourDateTime());
        setLocation('');
        setIsOnline(false);
        setIsInPerson(false);
        setFollowersOnly(false);
        setSubscribersOnly(false);
        setAllowRepeatRequests(true);
        setAdditionalInfo('');
        setCost(0);
        setDefaultSongSet('');
        setStartedAt(null);
        setEndedAt(null);
        setErrors({});
      }
    }, [bandEvent, isOpen]);

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="modalContent" overlayClassName="modalOverlay">
      <h2>{bandEvent ? t('events.form.editHeading') : t('events.form.heading')}</h2>
      <form>
        <div className="form-group">
          <label>{t('events.form.titleLabel')}</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
          {errors.title && <span className="error">{errors.title}</span>}
        </div>
        <div className="form-group">
          <label>{t('events.form.dateTimeLabel')}</label>
          <input type="datetime-local" value={dateTime} onChange={(e) => setDateTime(e.target.value)} required />
          {errors.dateTime && <span className="error">{errors.dateTime}</span>}
          {/* Allowed, for backfilling a show that happened, but said (ENG-511):
              a past event is listed under Past Events and can't be edited there. */}
          {dateTime && new Date(dateTime).getTime() < Date.now() && (
            <p className="form-warning" role="status" data-testid="event-past-date-warning">{t('events.form.pastDateWarning')}</p>
          )}
        </div>
        <div className="form-group-row">
            <div className="form-group">
            <label>
                {t('events.form.onlineStreamLabel')}
                <Switch isOn={isOnline} handleToggle={() => setIsOnline(!isOnline)} switchId="online-switch" />
            </label>
            </div>
        
            <div className="form-group">
            <label>
                {t('events.form.inPersonLabel')}
                <Switch isOn={isInPerson} handleToggle={() => setIsInPerson(!isInPerson)} switchId="inPerson-switch" />
            </label>
            </div>
            {errors.eventType && <span className="error">{errors.eventType}</span>}
        </div>
        {isInPerson && (
            <div>
                <div className="form-group">
                <label>{t('events.form.locationLabel')}</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} required />
                {errors.location && <span className="error">{errors.location}</span>}
                </div>
            </div>
        )}

        <div className="form-group">
          <label>
            {t('events.form.allowRepeatRequestsLabel')}
            <Switch isOn={allowRepeatRequests} handleToggle={() => setAllowRepeatRequests(!allowRepeatRequests)} switchId="repeat-requests-switch" />
          </label>
        </div>

        <button
          type="button"
          className="btn-toggle-advanced"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <FaChevronDown className={`btn-toggle-advanced-icon${showAdvanced ? ' open' : ''}`} />
          <span className="btn-toggle-advanced-line" />
          <span className="btn-toggle-advanced-label">{t('events.form.moreOptions')}</span>
          <span className="btn-toggle-advanced-line" />
        </button>

        {showAdvanced && (
          <>
            {isOnline && (
              <div className="form-group-row">
                <div className="form-group">
                  <label>
                    {t('events.form.followersOnlyLabel')}
                    <Switch isOn={followersOnly} handleToggle={() => setFollowersOnly(!followersOnly)} switchId="followers-switch" />
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    {t('events.form.subscribersOnlyLabel')}
                    <Switch isOn={subscribersOnly} handleToggle={() => setSubscribersOnly(!subscribersOnly)} switchId="subscribers-switch" />
                  </label>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{t('events.form.additionalInfoLabel')}</label>
              <textarea value={additionalInfo} onChange={(e) => setAdditionalInfo(e.target.value)} />
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label>{t('events.form.songSetTemplateLabel')}</label>
                <select value={defaultSongSet} onChange={(e) => setDefaultSongSet(e.target.value)}>
                  <option value="">{t('common.status.none')}</option>
                  {/* Add options dynamically */}
                </select>
              </div>
              <div className="form-group">
                <label>{t('events.form.admissionPriceLabel')}</label>
                <input type="number" min="0" step="0.01" pattern="^\d+(?:\.\d{1,2})?$" value={cost ?? ''} placeholder={t('events.form.admissionPricePlaceholder')} onChange={(e) => setCost(e.target.value)} aria-invalid={errors.cost ? 'true' : undefined} />
                {errors.cost && <span className="error">{errors.cost}</span>}
              </div>
            </div>
          </>
        )}

        {saveError && (
          <p className="modal-error" role="alert">{saveError}</p>
        )}

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="button-group">
            <button type="button" onClick={handleSave} className="btn-save">{t('common.actions.save')}</button>
            <button type="button" onClick={onClose} className="btn-cancel">{t('common.actions.cancel')}</button>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditEventModal;